import type { VectorSearchParams } from "./data_driver";
import type { ComputedSortField, SearchMatch } from "../types/search";
import { Entity, EntityValues } from "../types/entities";
import { WhereFilterOp, FieldPath, NonColumnFieldPath, FilterValues, NullsPlacement, OrderBySpec, RelationAggregateSort } from "../types/filter-operators";

/**
 * The element type of an array column, and the column's own type otherwise.
 *
 * A generated SDK emits an `array` property as `Array<X>` and a to-many
 * relation as `Array<TargetRow>`, so this is what `array-contains` compares
 * against on either.
 */
export type ElementOf<T> = T extends readonly (infer E)[] ? E : T;

/**
 * The `id` of a row-shaped element, and `never` for anything else.
 *
 * A to-many relation is emitted as `Array<TargetRow>`, but the filter compilers
 * compare a relation by **id** — `buildRelationFilterPredicate` in
 * `@rebasepro/server-postgres` unwraps a relation value down to its id — so
 * `where("tags", "array-contains", tagId)` is the call that works, and the
 * element type alone would refuse it.
 */
export type IdOf<E> = E extends { id: infer I } ? I : never;

/**
 * One member of an array column: its element, or — when the element is a row —
 * that row's id, which is what a relation filter is actually compared against.
 */
export type WhereElementOf<T> = ElementOf<T> | IdOf<ElementOf<T>>;

/**
 * The value a given operator takes on a column of type `T`.
 *
 * `WhereValue<T>` was one value type for all sixteen operators, which made
 * `array-contains` uncallable from a generated SDK — it is the one operator
 * whose value is an *element* of the column rather than the column's own type,
 * so on `tags: string[]` it wanted a `string[]` and the documented
 * `.where("tags", "array-contains", "featured")` was a compile error. The
 * spelling that did compile, `["featured"]`, builds `@> ARRAY[$1]` with the
 * whole array bound as the single element and matches nothing: the correct
 * query rejected, the accepted query silently wrong.
 *
 * The branches mirror `buildSingleFilterCondition` in `@rebasepro/server-postgres`:
 *
 * - `array-contains` → one element of the column (or a related row's id).
 * - `in` / `not-in` / `array-contains-any` → a list of elements; a bare element
 *   is read as the one-element list, and `null` is a null check.
 * - `like` / `ilike` / `not-like` / `not-ilike` → a SQL pattern. Always a
 *   string, including on numeric and date columns, which the driver casts.
 * - `is-null` / `is-not-null` → nothing; the value is ignored everywhere.
 * - everything else → the column's own type, or `null` for a null comparison.
 *
 * Distributes over `Op`, so a caller holding an unnarrowed `WhereFilterOp`
 * (a dynamic filter UI, say) gets the union of every branch and stays as
 * permissive as it was.
 */
export type WhereValueFor<Op extends WhereFilterOp, T> =
    Op extends "array-contains"
        ? WhereElementOf<T>
        : Op extends "in" | "not-in" | "array-contains-any"
            ? readonly WhereElementOf<T>[] | WhereElementOf<T> | null
            : Op extends "like" | "ilike" | "not-like" | "not-ilike"
                ? string
                : Op extends "is-null" | "is-not-null"
                    ? null | undefined
                    : T | null;

/**
 * A group of conditions combined with `and`, `or`, or negated with `not`.
 *
 * ## `not`
 *
 * `not` negates the **conjunction** of its `conditions`: `not(a)` is `NOT a`,
 * and `not(a, b)` is `NOT (a AND b)`. One rule, stated here and applied
 * identically by the wire codec (`or(...)`/`and(...)`/`not(...)` in
 * `@rebasepro/common`), the REST `?not=` parameter and every driver compiler,
 * so a negation means the same thing whichever end writes it.
 *
 * Negation is not expressible by inverting the operators inside the group: SQL
 * three-valued logic makes `NOT (a AND b)` and `(NOT a) OR (NOT b)` differ the
 * moment a NULL is involved, and only one of them is what the caller wrote. It
 * compiles to a real `NOT (...)`.
 */
export interface LogicalCondition {
    type: "and" | "or" | "not";
    conditions: (FilterCondition | LogicalCondition)[];
}

export interface FilterCondition {
    column: string;
    operator: WhereFilterOp;
    value: unknown;
}

/**
 * How one relation is loaded by {@link FindParams.include}.
 *
 * `true` loads the relation whole. The object form narrows it — the same four
 * knobs a top-level query has, applied to the rows *inside* one relation — and
 * `include` nests, so a query can ask for "each post's five newest published
 * comments, each with its author" in one request.
 *
 * ```ts
 * include: {
 *   comments: {
 *     limit: 5,
 *     where: { published: ["==", true] },
 *     orderBy: ["created_at", "desc"],
 *     include: { author: true }
 *   }
 * }
 * ```
 *
 * Nesting is bounded at {@link MAX_INCLUDE_DEPTH} hops. Each hop is another
 * batched query, and the bound is what stops one request from walking a
 * self-referencing relation forever.
 *
 * @group Data
 */
export interface IncludeOptions {
    /** Rows to load per parent row. Applied per parent, not across the page. */
    limit?: number;
    /** Filter the related rows, in the same dialect as {@link FindParams.where}. */
    where?: FilterValues<string>;
    /** An `and`/`or`/`not` group over the related rows. */
    logical?: LogicalCondition;
    /**
     * Sort the related rows — the tuple form, or the `field:direction[:nulls]`
     * shorthand the REST `?orderBy=` parameter uses.
     *
     * The string is accepted because this whole object travels over a query
     * string, where a tuple is three characters of JSON heavier for no gain.
     */
    orderBy?: OrderBySpec<string> | string;
    /** Columns of the *related* row to return. `id` is always included. */
    fields?: string[];
    /** Relations of the related row to load in turn. */
    include?: IncludeSpec;
}

/**
 * The relations a read loads, as a list of (possibly dotted) names or as a
 * tree.
 *
 * - `["author", "comments.author"]` — a dotted path is the same thing as the
 *   nested object form, spelled flat. It is what the REST `?include=` parameter
 *   carries, and the two forms compile to the same request.
 * - `["*"]` — every relation, one hop deep. The admin panel's shape.
 * - `{ comments: { limit: 5, include: { author: true } } }` — the parametrised
 *   form.
 *
 * A name that is not a relation of the collection is a **400
 * `UNKNOWN_RELATION`**, not a silent omission: a read that quietly drops an
 * `include` answers 200 with the field missing, which is indistinguishable from
 * a row that genuinely has no related row.
 *
 * @group Data
 */
export type IncludeSpec = string[] | Record<string, true | IncludeOptions>;

/**
 * Hops an {@link IncludeSpec} may nest. `comments.author` is two.
 *
 * @group Data
 */
export const MAX_INCLUDE_DEPTH = 3;

/**
 * Parameters for querying a collection.
 *
 * ## How the filter parameters combine
 *
 * `where`, `logical`, and `searchString` are **independent** and, when more
 * than one is present, are combined with **AND** — every clause must match.
 * Concretely the backend builds:
 *
 * ```text
 * (where filters, AND-ed together)
 *   AND (logical group)
 *   AND (searchString matches, OR-ed across searchable columns)
 * ```
 *
 * So `where` does **not** conflict with or override `logical` — they stack.
 * If you need `where` fields OR-ed with each other, move them into `logical`
 * instead. There is no way to OR `where` against `logical`; express anything
 * that isn't a plain AND of the three groups inside a single `logical` tree.
 *
 * ## Pagination precedence
 *
 * `limit`/`offset` and `page` describe the same window two ways. If **both
 * `offset` and `page` are provided, `page` wins** — the backend computes
 * `offset = (page - 1) * (limit ?? DEFAULT_LIST_LIMIT)` and ignores the
 * explicit `offset`. Pick one style per query.
 *
 * @group Data
 */
export interface FindParams<M extends Record<string, unknown> = Record<string, unknown>> {
    /**
     * Maximum number of items to return.
     *
     * Omit it and the backend applies {@link DEFAULT_LIST_LIMIT}, so a read is
     * never unbounded. Provide it and it must be a whole number between 1 and
     * {@link MAX_LIST_LIMIT}: the backend **rejects** anything else with a 400
     * rather than trimming it to fit, because a page quietly smaller than the
     * one you asked for is indistinguishable from having reached the end of the
     * collection. To read past the ceiling, page with `offset` — or let
     * {@link SDKCollectionClient.iterate} / {@link SDKCollectionClient.findAll}
     * do it for you.
     */
    limit?: number;
    /**
     * Number of items to skip. Ignored when {@link FindParams.page} is also
     * set — `page` takes precedence.
     */
    offset?: number;
    /**
     * Page number (1-indexed), alternative to {@link FindParams.offset}.
     * When set, overrides `offset` as `(page - 1) * (limit ?? DEFAULT_LIST_LIMIT)`.
     */
    page?: number;
    /**
     * Filter conditions keyed by field name.
     * Each value is a `[WhereFilterOp, value]` tuple or an array of tuples
     * for multiple conditions on the same field. Multiple fields, and multiple
     * tuples on one field, are **AND-ed**; also AND-ed with `logical` and
     * `searchString` when present (see the interface docs).
     *
     * @example
     * { status: ["==", "active"] }
     * { age: [">=", 18] }
     * { role: ["in", ["admin", "editor"]] }
     * { age: [[">=", 18], ["<", 65]] }
     */
    where?: FilterValues<FieldPath<M>>;
    /**
     * Logical grouping conditions (AND/OR). Use this for anything `where`
     * can't express — notably OR-ing conditions. AND-ed with `where` and
     * `searchString` when present (see the interface docs).
     */
    logical?: LogicalCondition;
    /**
     * Sort order as a `[field, direction]` tuple, or a list of them applied in
     * order of significance — the second key breaks ties on the first, and so on.
     *
     * @example orderBy: ["created_at", "desc"]
     * @example orderBy: [["roles", "asc"], ["created_at", "desc"]]
     */
    orderBy?: OrderBySpec<FieldPath<M> | ComputedSortField>;
    /**
     * Relations to load into the response — see {@link IncludeSpec}.
     *
     * Not checked against `M` here: a relation name comes from the collection's
     * `relations`, not from its columns, so nothing in a *hand-written* row type
     * can validate one. A **generated** `Database` narrows this to the
     * collection's actual relation keys, recursively — see `rebase codegen`.
     *
     * An unknown name is a 400 `UNKNOWN_RELATION`. It used to be ignored.
     */
    include?: IncludeSpec;

    /**
     * Columns to return, instead of all of them.
     *
     * A real column projection: only these columns are read from the database,
     * so a query that needs two fields of a wide row does not pay for the rest.
     * `excludeFromApi` still applies — naming such a column here does not
     * un-hide it — and the primary key is always returned, because a row that
     * cannot be addressed cannot be updated, deleted or paged past.
     *
     * A relation named in {@link FindParams.include} is loaded regardless of
     * whether it appears here; use {@link IncludeOptions.fields} to narrow the
     * columns *within* an included relation.
     */
    fields?: string[];

    /**
     * Collapse rows that are identical over the columns being returned.
     *
     * `SELECT DISTINCT` over the projection — so it is only meaningful
     * alongside {@link FindParams.fields}, and with the primary key in the
     * projection (which it always is) every row is already distinct. Pair it
     * with `fields` naming the columns you actually want the distinct values of.
     *
     * `meta.total` counts distinct rows too, so a distinct listing's `hasMore`
     * describes the set it is paging.
     */
    distinct?: boolean;

    /**
     * Continue from where a previous page ended — keyset ("seek") pagination.
     *
     * The value is the opaque `meta.nextCursor` of the previous response. It
     * encodes the sort keys the query was ordered by and the last row's values
     * for them, so a page picks up strictly after the last row served rather
     * than at a row *count* that concurrent writes have already moved.
     *
     * It has to describe the same query: `after` alongside a different
     * `orderBy` is a 400 `CURSOR_ORDER_MISMATCH` rather than a page of rows
     * seeked in an order nobody asked for. Mutually exclusive with `offset` and
     * `page` for the same reason.
     *
     * Multi-key sorts and nullable keys both work — the comparison is built
     * over every key, in order, with the NULL placement the sort declared.
     */
    after?: string;
    /**
     * Text search string, AND-ed with `where`/`logical`. This is the value
     * behind the query builder's `.search()` method.
     *
     * What it compiles to depends on the collection. By default — matching
     * every collection that has not said otherwise — it is a case-insensitive
     * substring match OR-ed across the collection's top-level `string`
     * properties: it does not reach inside `map` or `array` properties, it does
     * not stem or rank, and it cannot use an index.
     *
     * A Postgres collection that declares a `search` block instead gets a
     * ranked full-text match over exactly the fields it named, and rows come
     * back with a {@link FindParams.orderBy}-able `_score`.
     */
    searchString?: string;

    /**
     * Nearest-neighbour search over a `vector` property.
     *
     * Postgres only, and only for a collection that declares a property of
     * type `vector`. Rows come back ordered by distance, closest first, each
     * carrying a `_distance`. Combines with `where` and `logical`, which are
     * applied as filters before the ordering — so this is "the nearest rows
     * that also match", not "the nearest rows, then filtered".
     *
     * Supplying the query vector is the caller's job: rebase stores and
     * searches embeddings, it does not compute them.
     */
    vectorSearch?: VectorSearchParams;

    /**
     * Ask each returned row to explain itself: which declared search fields
     * matched, with a highlighted snippet from each. Populates `_matches`.
     *
     * Off by default because it is not free — one `ts_headline` per declared
     * field per returned row, and `ts_headline` re-parses the document rather
     * than reading the index. Fine for a page of results, not for an export.
     *
     * Ignored unless the collection declares a `search` block and the query
     * carries a `searchString`; there is nothing to explain otherwise.
     */
    searchExplain?: boolean;
}

/**
 * Paginated response from a collection query.
 * @group Data
 */
export interface FindResponse<M extends Record<string, unknown> = Record<string, unknown>> {
    /** Array of entities matching the query */
    data: Entity<M>[];
    /** Pagination metadata */
    meta: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
}



/**
 * Fluent query builder for the **admin panel** — resolves to `FindResponse<M>`
 * (Snapshot-wrapped rows).
 *
 * @internal App developers should use {@link SDKQueryBuilderInterface}
 * (flat rows, returned by `client.data.*` / `context.data.*`). This
 * Snapshot-flavored variant backs the admin panel internals only.
 *
 * @group Data
 */
export interface QueryBuilderInterface<M extends Record<string, unknown> = Record<string, unknown>> {
    where<K extends keyof M & string, Op extends WhereFilterOp>(column: K, operator: Op, value: WhereValueFor<Op, M[K]>): this;
    where(logicalCondition: LogicalCondition): this;
    orderBy(column: (keyof M & string) | ComputedSortField, direction?: "asc" | "desc"): this;
    limit(count: number): this;
    offset(count: number): this;
    search(searchString: string, options?: { explain?: boolean }): this;

    /**
     * Order rows by nearest-neighbour distance to `vector`, closest first.
     *
     * Postgres only, over a property declared as `type: "vector"`. Each row
     * comes back with a `_distance`. Any `where` on the same query filters
     * before the ordering; distance decides the order.
     *
     * The query embedding is the caller's to produce.
     */
    vectorSearch(
        property: string,
        vector: number[],
        options?: { distance?: "cosine" | "l2" | "inner_product"; threshold?: number }
    ): this;
    include(...relations: string[]): this;
    find(): Promise<FindResponse<M>>;
    listen(onUpdate: (data: FindResponse<M>) => void, onError?: (error: Error) => void): () => void;
}

/**
 * A single collection's CRUD accessor for the **admin panel** — every method
 * resolves to `Snapshot`-wrapped rows (`FindResponse<M>` / `Snapshot<M>`).
 *
 * @internal App developers do **not** use this. The public, symmetric surface
 * is {@link SDKCollectionClient} (flat rows), exposed as `client.data.products`
 * in the SDK and `context.data.products` in framework callbacks. This
 * Snapshot-flavored accessor backs the admin panel view-model only.
 *
 * @group Data
 */
export interface CollectionAccessor<M extends Record<string, unknown> = Record<string, unknown>> {
    /**
     * Find multiple records with optional filtering, pagination, and sorting.
     */
    find(params?: FindParams<M>): Promise<FindResponse<M>>;

    /**
     * Find a single record by its ID.
     */
    findById(id: string | number): Promise<Entity<M> | undefined>;

    /**
     * Create a new record.
     * @param data The entity data to create.
     * @param id Optional specific ID to use for the new record.
     * @returns The created entity
     */
    create(data: Partial<EntityValues<M>>, id?: string | number): Promise<Entity<M>>;

    /**
     * Create many records in a single transaction.
     *
     * See {@link SDKCollectionClient.createMany}. Optional: not every driver can
     * write in bulk, and callers should fall back to `create` per record.
     */
    createMany?(
        data: Partial<EntityValues<M>>[],
        options?: { upsert?: boolean; onConflict?: readonly string[] }
    ): Promise<Entity<M>[]>;

    /**
     * Update an existing record by ID.
     * @returns The updated entity
     */
    update(id: string | number, data: Partial<EntityValues<M>>): Promise<Entity<M>>;

    /**
     * Update many records in a single transaction.
     *
     * See {@link SDKCollectionClient.updateMany}. Optional, as `createMany` is.
     */
    updateMany?(updates: { id: string | number; data: Partial<EntityValues<M>> }[]): Promise<Entity<M>[]>;

    /**
     * Delete many records in a single transaction.
     *
     * See {@link SDKCollectionClient.deleteMany}. Optional, as `createMany` is.
     */
    deleteMany?(ids: (string | number)[]): Promise<void>;

    /**
     * Delete a record by ID.
     */
    delete(id: string | number): Promise<void>;

    /**
     * Subscribe to a collection for real-time updates.
     * Optional method, may not be supported by all implementations (like stateless HTTP clients).
     */
    listen?(params: FindParams<M> | undefined, onUpdate: (response: FindResponse<M>) => void, onError?: (error: Error) => void): () => void;

    /**
     * Subscribe to a single record for real-time updates.
     * Optional method.
     */
    listenById?(id: string | number, onUpdate: (entity: Entity<M> | undefined) => void, onError?: (error: Error) => void): () => void;

    /**
     * Count the number of records matching the given filter.
     *
     * Optional on this contract because a data source need not support it, and
     * required on `CollectionClient` — the HTTP implementation always has it.
     * So `client.data.posts.count()` compiles in the browser while the same
     * call through a `context.data` accessor needs `count?.()`, which is the
     * one place the two halves of this API are not interchangeable.
     */
    count?(params?: FindParams<M>): Promise<number>;

    /**
     * {@link SDKCollectionClient.aggregate}. Optional here for the same reason
     * `count` is: not every data source can compute one, and the SDK wraps an
     * absent implementation in a stub that says so rather than returning a
     * number nothing counted.
     */
    aggregate?(params: AggregateParams<M>): Promise<AggregateRow[]>;

    // Fluent Query Builder
    where<K extends keyof M & string, Op extends WhereFilterOp>(column: K, operator: Op, value: WhereValueFor<Op, M[K]>): QueryBuilderInterface<M>;
    where(logicalCondition: LogicalCondition): QueryBuilderInterface<M>;
    orderBy(column: (keyof M & string) | ComputedSortField, direction?: "asc" | "desc"): QueryBuilderInterface<M>;
    limit(count: number): QueryBuilderInterface<M>;
    offset(count: number): QueryBuilderInterface<M>;
    search(searchString: string, options?: { explain?: boolean }): QueryBuilderInterface<M>;

    /**
     * Order rows by nearest-neighbour distance to `vector`, closest first.
     *
     * Postgres only, over a property declared as `type: "vector"`. Each row
     * comes back with a `_distance`. Any `where` on the same query filters
     * before the ordering; distance decides the order.
     *
     * The query embedding is the caller's to produce.
     */
    vectorSearch(
        property: string,
        vector: number[],
        options?: { distance?: "cosine" | "l2" | "inner_product"; threshold?: number }
    ): QueryBuilderInterface<M>;
    include(...relations: string[]): QueryBuilderInterface<M>;
}

// =============================================================================
// SDK-facing types — flat rows, no Entity wrapper
// =============================================================================

/**
 * Pagination metadata returned with collection queries.
 * @group Data
 */
export interface PaginationMeta {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    /**
     * The opaque cursor that continues this listing — pass it back as
     * {@link FindParams.after}.
     *
     * Present whenever there is a next page to describe (`hasMore` is true and
     * the page returned at least one row). Absent on the last page, and absent
     * on a query no cursor can describe (relevance ordering, whose scores are
     * computed per query and are not comparable between two of them).
     *
     * Opaque on purpose: it encodes the sort keys *and* the last row's values
     * for them, and a client that parsed it would be depending on an encoding
     * that exists to be changed.
     */
    nextCursor?: string;
}

/**
 * Paginated response from a collection query (SDK-facing).
 * Returns flat rows instead of Entity-wrapped objects.
 *
 * @example
 * const { data, meta } = await rebase.data.posts.find();
 * console.log(data[0].title); // direct access — no .values
 * console.log(meta.total);
 *
 * @group Data
 */
export interface FindResult<M extends Record<string, unknown> = Record<string, unknown>> {
    /**
     * Flat rows matching the query, each carrying whatever the query computed
     * for it — see {@link QueryComputedFields}.
     */
    data: (M & QueryComputedFields)[];
    /** Pagination metadata */
    meta: PaginationMeta;
}

/**
 * Values a query attaches to a row that are not columns of it.
 *
 * Both are absent unless the query asked for the thing that produces them, so
 * both are optional — and reading one on a query that did not ask returns
 * `undefined` rather than a wrong number.
 *
 * They live here rather than on the row type because a generated row type
 * describes a *table*, and neither of these is in one. Without this, a caller
 * who sorted by relevance could not then read the relevance.
 *
 * A `type` alias, deliberately, not an `interface`. TypeScript grants an
 * implicit index signature to a type alias and withholds it from an interface,
 * so `Row & QueryComputedFields` stops being assignable to
 * `Record<string, unknown>` the moment this becomes an interface. Seven casts
 * in one downstream app broke on exactly that.
 *
 * @group Data
 */
export type QueryComputedFields = {
    /**
     * Relevance, when the collection declares a {@link SearchConfig} and the
     * query carried a search string. Higher is better; the scale is not
     * comparable between two different search strings.
     */
    _score?: number;
    /**
     * Which declared fields matched, and the text around each hit. Present only
     * when the query asked for it — `.search(term, { explain: true })` — because
     * it costs a `ts_headline` per field per row.
     */
    _matches?: SearchMatch[];
    /**
     * Distance to the query vector, when the query used
     * {@link FindParams.vectorSearch}. Lower is closer, and the rows are
     * already ordered by it.
     */
    _distance?: number;
};

/**
 * One aggregate a query asks for.
 *
 * `count` alone counts rows; every other function names a column, and `count`
 * with a column counts its non-NULL values.
 *
 * The result key is derived rather than chosen: `sum(total)` comes back as
 * `sum_total` and a bare `count()` as `count`. Letting a caller name it would
 * mean checking their name is not also a `groupBy` field — a rule nobody would
 * guess, and a silently overwritten value if it went unchecked.
 *
 * @group Data
 */
export type AggregateSelect<M extends Record<string, unknown> = Record<string, unknown>> =
    | { fn: "count"; field?: Extract<keyof M, string> }
    | { fn: "sum" | "avg" | "min" | "max"; field: Extract<keyof M, string> };

/**
 * One row of an aggregate result: the `groupBy` columns, plus one key per
 * {@link AggregateSelect} under its derived alias.
 *
 * `count`, `sum` and `avg` arrive as numbers — Postgres returns bigint and
 * numeric as strings, and they are parsed once at the driver rather than by
 * every caller. `min`/`max` keep the column's own type.
 *
 * @group Data
 */
export type AggregateRow = Record<string, unknown>;

/**
 * What {@link SDKCollectionClient.aggregate} takes: the same narrowing a
 * `find()` takes, minus the parts of it that describe a *page* of rows.
 *
 * `limit` survives and means what it means on the REST route — a bound on the
 * number of **groups**, because grouping by a high-cardinality column is a whole
 * table's worth of rows in one response. It is ignored when there is no
 * `groupBy`, since an ungrouped aggregate is one row.
 *
 * `orderBy`, `include`, `after` and the rest are absent on purpose: an
 * aggregate has no rows to sort, no relations to load and no page to continue.
 * They were silently ignored on the REST route; here they do not typecheck.
 *
 * @group Data
 */
export interface AggregateParams<M extends Record<string, unknown> = Record<string, unknown>> {
    /** The aggregates to compute. At least one. */
    select: AggregateSelect<M>[];
    /** Columns to group by. Omit for a single row over everything that matches. */
    groupBy?: Extract<keyof M, string>[];
    /** Filter conditions, as {@link FindParams.where}. */
    where?: FilterValues<FieldPath<M>>;
    /** An `and`/`or`/`not` group, AND-ed with `where`. */
    logical?: LogicalCondition;
    /** Text search, AND-ed with the filters. */
    searchString?: string;
    /** Most groups to return. Ignored without `groupBy`. */
    limit?: number;
}

/**
 * Which column an iteration seeks on, for keyset ("seek") pagination.
 *
 * Either the column name on its own — sorted ascending — or the column plus an
 * explicit direction. The column must be **unique** and must be the column the
 * query is ordered by; see {@link PageWalkOptions.cursor}.
 *
 * @group Data
 */
export type CursorSpec<M extends Record<string, unknown> = Record<string, unknown>> =
    | (Extract<keyof M, string>)
    | { field: Extract<keyof M, string>; direction?: "asc" | "desc" };

/**
 * How {@link SDKCollectionClient.iterate} / {@link SDKCollectionClient.findAll}
 * walk a collection, layered on top of the normal `find()` parameters.
 *
 * @group Data
 */
export interface PageWalkOptions<M extends Record<string, unknown> = Record<string, unknown>> {
    /**
     * Rows fetched per request. Defaults to 200; values below 1 are clamped up.
     * This is the request size, not a result cap — the iteration keeps going
     * until the server says there is nothing left.
     */
    pageSize?: number;
    /**
     * Paginate by **seeking on a column** instead of by offset.
     *
     * Offset paging — the default — re-counts rows on every request, so a row
     * inserted or deleted *while the iteration runs* shifts the window and the
     * walk silently skips or repeats rows. Seeking is immune to that: each page
     * asks for rows strictly after the last one seen, so concurrent writes
     * before the cursor cannot move it.
     *
     * Prefer this whenever the collection has a unique, sortable column
     * (typically its primary key). The column must be unique — a repeated value
     * at a page boundary either skips rows or stalls, and the iterator throws
     * rather than looping — and the query is ordered by it, so a `cursor` and a
     * conflicting `orderBy` is an error, not a silent override.
     *
     * Implemented with the parameters `find()` already takes (an `orderBy` plus
     * a `>` / `<` filter on the cursor column), so it works on every transport
     * and needs nothing new from the server.
     *
     * @example
     * for await (const job of client.data.jobs.iterate({ cursor: "id" })) { … }
     */
    cursor?: CursorSpec<M>;
    /**
     * Hard ceiling on the number of requests one walk may make, so a server
     * that never stops saying `hasMore` cannot spin forever. Defaults to
     * 10 000 pages; hitting it throws.
     */
    maxPages?: number;
}

/**
 * Parameters accepted by {@link SDKCollectionClient.iterate} — everything
 * `find()` takes except the window itself (`limit`, `offset`, `page`), which
 * the iterator owns, plus the walk options.
 *
 * @group Data
 */
export type IterateParams<M extends Record<string, unknown> = Record<string, unknown>> =
    Omit<FindParams<M>, "limit" | "offset" | "page"> & PageWalkOptions<M>;

/**
 * Parameters accepted by {@link SDKCollectionClient.findAll}: the iteration
 * parameters plus the ceiling that keeps a whole collection from being pulled
 * into memory unnoticed.
 *
 * @group Data
 */
export type FindAllParams<M extends Record<string, unknown> = Record<string, unknown>> =
    IterateParams<M> & {
        /**
         * Most rows to materialise. Defaults to 10 000. Exceeding it **throws**
         * — a truncated array returned as if it were the whole answer is the
         * kind of quiet wrong that shows up months later in a report. Pass
         * `Infinity` to opt out deliberately, or use `iterate()` to stream.
         */
        maxRows?: number;
    };

/**
 * Fluent Query Builder Interface for the SDK client.
 * Returns `FindResult<M>` (flat rows) instead of `FindResponse<M>` (Entity-wrapped).
 *
 * @group Data
 */
export interface SDKQueryBuilderInterface<M extends Record<string, unknown> = Record<string, unknown>> {
    where<K extends keyof M & string, Op extends WhereFilterOp>(column: K, operator: Op, value: WhereValueFor<Op, M[K]>): this;
    /**
     * Filter on a relation path (`author.name`) or a JSON path
     * (`metadata->>tier`).
     *
     * A separate overload because the value cannot be typed: neither addresses
     * a column of `M`, so there is nothing in a generated row type to check
     * against — the driver resolves the path and refuses what it cannot. The
     * key is still constrained to a *path*, so a mistyped column name does not
     * fall through to here and lose its check.
     *
     * `find({ where })` has accepted both all along ({@link FieldPath}); the
     * builder did not, so the documented relation-path filters were compile
     * errors on a typed client.
     */
    where(column: NonColumnFieldPath, operator: WhereFilterOp, value: unknown): this;
    where(logicalCondition: LogicalCondition): this;
    /**
     * Sort by a column, a relation or JSON path, `_score`, or an aggregate over
     * a to-many relation — the same key set {@link FindParams.orderBy} takes.
     */
    orderBy(
        column: FieldPath<M> | ComputedSortField | RelationAggregateSort,
        direction?: "asc" | "desc",
        nulls?: NullsPlacement
    ): this;
    limit(count: number): this;
    offset(count: number): this;
    search(searchString: string, options?: { explain?: boolean }): this;

    /**
     * Order rows by nearest-neighbour distance to `vector`, closest first.
     *
     * Postgres only, over a property declared as `type: "vector"`. Each row
     * comes back with a `_distance`. Any `where` on the same query filters
     * before the ordering; distance decides the order.
     *
     * The query embedding is the caller's to produce.
     */
    vectorSearch(
        property: string,
        vector: number[],
        options?: { distance?: "cosine" | "l2" | "inner_product"; threshold?: number }
    ): this;
    /**
     * Load relations — names, dotted paths (`"comments.author"`), or the
     * parametrised tree. Repeated calls merge rather than replace.
     */
    include(...relations: (string | IncludeSpec)[]): this;
    /**
     * Return only these columns. A real projection: the columns are what is
     * read from the database, not what survives a trim of the response.
     */
    fields(...columns: (FieldPath<M> | string)[]): this;
    /** `SELECT DISTINCT` over the projection — see {@link FindParams.distinct}. */
    distinct(enabled?: boolean): this;
    /** Continue after a previous page's `meta.nextCursor`. */
    after(cursor: string): this;
    find(): Promise<FindResult<M>>;
    /**
     * Aggregate the rows this query matches instead of returning them.
     *
     * The builder's `where`/`logical`/`search` narrow which rows are
     * aggregated; its `orderBy`, `include` and window do not apply and are
     * ignored, exactly as they are on the REST route.
     */
    aggregate(params: Omit<AggregateParams<M>, "where" | "logical" | "searchString">): Promise<AggregateRow[]>;

    /**
     * Page through everything this query matches, one row at a time.
     *
     * The same walker {@link SDKCollectionClient.iterate} uses, so the ceiling
     * on `limit` is not a ceiling on what a query can read. `.limit()` set on
     * the builder becomes the **page size** here, not a total.
     */
    iterate(options?: PageWalkOptions<M>): AsyncIterableIterator<M>;

    /**
     * Collect everything this query matches into one array.
     *
     * {@link SDKCollectionClient.findAll}'s `maxRows` guard applies: an
     * unbounded collect is a memory hazard, so it stops and says so rather than
     * growing until the process dies.
     */
    findAll(options?: PageWalkOptions<M> & { maxRows?: number }): Promise<M[]>;

    count(): Promise<number>;
    listen(onUpdate: (data: FindResult<M>) => void, onError?: (error: Error) => void): () => void;
}

/**
 * SDK collection client — returns flat rows, no Entity wrapper.
 *
 * This is the public API surface for app developers using
 * `createRebaseClient()`. admin internals use `CollectionAccessor` instead.
 *
 * Type parameters:
 * - `M` — the **Row** shape returned by reads (`find`, `findById`, `listen`).
 * - `I` — the **Insert** shape accepted by {@link create}. Defaults to
 *   `Partial<M>`; the generated SDK supplies a dedicated `Insert` type where
 *   required columns are required and auto-generated / read-only columns are
 *   omitted, so `create({})` on a table with required fields is a compile error.
 * - `U` — the **Update** shape accepted by {@link update}. Defaults to
 *   `Partial<M>`; the generated SDK supplies a dedicated `Update` type.
 *
 * @example
 * const { data: posts } = await rebase.data.posts.find();
 * console.log(posts[0].title);       // flat access
 * console.log(posts[0].id);          // id at top level
 *
 * const post = await rebase.data.posts.findById(1);
 * console.log(post?.title);          // no .values needed
 *
 * @group Data
 */
/**
 * A change expressed as an operation on the column's current value, rather than
 * as the value to store.
 *
 * `{ views: 5 }` says what the number becomes; `{ views: { $inc: 1 } }` says
 * what happens to it. The difference is the read the caller no longer has to
 * make — and the race that read opens. Two requests that each read `4`, add one
 * and write `5` lose an increment between them; `SET views = views + 1` cannot,
 * because the arithmetic happens inside the statement holding the row lock.
 *
 * Exactly one operator per field. `{ views: { $inc: 1, $push: "x" } }` is
 * refused rather than applied in an order the caller cannot see.
 *
 * @group Data
 */
/**
 * The operator names, as a value.
 *
 * A runtime list beside the type because three layers have to *recognise* an
 * operation, not just accept one: the REST validator, the driver that compiles
 * it, and the offline queue that must refuse to apply one locally. Three copies
 * of four strings is three chances for one of them to miss an operator added to
 * the other two, and the failure is silent in the worst direction — an
 * unrecognised marker is written to the column as a JSON document.
 *
 * @group Data
 */
export const FIELD_OPERATORS = ["$inc", "$push", "$pull", "$merge"] as const;

/**
 * The key of a {@link BatchRef}. Declared here, beside the field operators,
 * because the two share one namespace: a `$`-prefixed key in a write payload is
 * a marker, and every reader of that namespace has to know all of it.
 *
 * @group Data
 */
export const BATCH_REF_KEY = "$ref";

/**
 * Whether a value is *trying* to be a field operation — including a misspelled
 * one, which is the case worth catching.
 *
 * Any `$`-prefixed key counts, because `{ $increment: 1 }` written to a number
 * column as a JSON document is the failure this exists to prevent. No collection
 * can declare a column whose value legitimately has a key beginning with `$`: a
 * `map` property's sub-keys are declared, and `$` is not valid in the
 * identifiers the DDL generators emit.
 *
 * The one exception is `{ $ref: … }`, the batch's backward reference. It stands
 * where a *value* goes and is resolved to one before the row is written, so it
 * is not an operation on a column — reading it as a misspelled operator refused
 * every `$ref` in a batch with "unknown field operator '$ref'".
 *
 * @group Data
 */
export function isFieldOperation(value: unknown): boolean {
    if (typeof value !== "object" || value === null || Array.isArray(value) || value instanceof Date) {
        return false;
    }
    const keys = Object.keys(value);
    if (keys.length === 1 && keys[0] === BATCH_REF_KEY) return false;
    return keys.some((key) => key.startsWith("$"));
}

/** True when any value in a write payload is (or is attempting to be) one. @group Data */
export function hasFieldOperation(values: Record<string, unknown> | undefined): boolean {
    return !!values && Object.values(values).some(isFieldOperation);
}

export type FieldOperation =
    /** Add to a `number` column; negative to subtract. `SET col = col + n`. */
    | { $inc: number }
    /** Append one value, or each of an array of values, to an `array` column. */
    | { $push: unknown }
    /** Remove every occurrence of a value from an `array` column. */
    | { $pull: unknown }
    /** Shallow-merge an object into a `map` column. `SET col = col || …::jsonb`. */
    | { $merge: Record<string, unknown> };

/**
 * The payload {@link SDKCollectionClient.update} accepts: plain values, field
 * operations, or both in one body.
 *
 * @group Data
 */
export type UpdateValues<U> = { [K in keyof U]?: U[K] | FieldOperation };

/**
 * Where an upsert looks for the row it might be replacing.
 *
 * The columns must carry a uniqueness guarantee the database can use as an
 * `ON CONFLICT` target — the primary key, a property with
 * `validation.unique`, or the columns of a declared `unique` index. Anything
 * else is refused with a 400 rather than sent to Postgres, which would answer
 * `there is no unique or exclusion constraint matching the ON CONFLICT
 * specification` from inside a transaction that has already done work.
 *
 * @group Data
 */
export interface UpsertOptions extends WriteOptions {
    /** Column names forming the conflict target. Defaults to the primary key. */
    onConflict?: readonly string[];
}

/**
 * A placeholder standing for a value only the server will know: the id of a row
 * an earlier operation in the same batch creates.
 *
 * `{ "$ref": "order.id" }` reads the field `id` off the result of the operation
 * that named itself `ref: "order"`. Without it a batch cannot express the one
 * thing a cross-collection batch exists for — writing a parent and its children
 * together — because the child's foreign key is not knowable until the parent
 * has been inserted, and splitting the two into separate requests is exactly
 * the non-atomic sequence the batch replaces.
 *
 * Only backward references resolve. `ref` names must be unique within a batch,
 * and an operation may not reference itself or anything after it.
 *
 * @group Data
 */
export interface BatchRef {
    /** `<ref name>.<field>`, e.g. `order.id`. */
    $ref: string;
}

/** One entry of a batch request. @group Data */
export type BatchOperation<DB = Record<string, unknown>> = {
    [K in Extract<keyof DB, string>]:
        | {
            op: "create";
            collection: K;
            values: { [F in keyof InsertOf<DB[K]>]?: InsertOf<DB[K]>[F] | BatchRef } & Record<string, unknown>;
            /** Name this row so a later operation can reference its columns. */
            ref?: string;
        }
        | {
            op: "upsert";
            collection: K;
            values: { [F in keyof InsertOf<DB[K]>]?: InsertOf<DB[K]>[F] | BatchRef } & Record<string, unknown>;
            /** See {@link UpsertOptions.onConflict}. Defaults to the primary key. */
            onConflict?: readonly string[];
            ref?: string;
        }
        | {
            op: "update";
            collection: K;
            id: string | number | BatchRef;
            values: { [F in keyof UpdateOf<DB[K]>]?: UpdateOf<DB[K]>[F] | FieldOperation | BatchRef } & Record<string, unknown>;
            ref?: string;
        }
        | {
            op: "delete";
            collection: K;
            id: string | number | BatchRef;
            ref?: string;
        };
}[Extract<keyof DB, string>];

/**
 * What `POST /api/data/_batch` answers with.
 *
 * `data` is aligned to `operations`: the written row for a create, upsert or
 * update, and `null` for a delete — so an index into one is an index into the
 * other, whatever the batch mixed.
 *
 * @group Data
 */
export interface BatchResult<R = Record<string, unknown>> {
    data: (R | null)[];
    meta: { operations: number };
}

/**
 * Per-request options for a write.
 * @group Data
 */
export interface WriteOptions {
    /**
     * Names this write, so re-sending it is recognised instead of repeated.
     *
     * A client that does not see a response cannot know whether the write
     * committed. Retrying is therefore the only option, and without a key the
     * server has no way to tell a retry from a second, genuinely new write — so
     * it performs it again. On a table with a server-assigned id that is a
     * duplicate row, because the id the client chose was never used.
     *
     * A key names **one** request, not a job. It records the method, the path
     * and the body it was claimed for, so re-sending that exact request replays
     * its answer, while the same key on a different one is refused with
     * `IDEMPOTENCY_KEY_REUSED` (422) rather than silently answered with the
     * first request's result. Pass a fresh key — a uuid — per call; a reusable
     * business id shared by the create and the delete of one import means the
     * second of them never runs.
     *
     * Set by the offline queue on every replay. Honoured for 24 hours and scoped
     * to the authenticated user — an unauthenticated caller has no principal to
     * scope it to, so the key is ignored there. A retry sent while the first
     * attempt is still being answered gets `IDEMPOTENCY_KEY_IN_PROGRESS` (409)
     * and should be sent again. A server that cannot store keys ignores the
     * header rather than refusing the write.
     */
    idempotencyKey?: string;

    /**
     * Whether the server should send the written row back.
     *
     * `false` sends `Prefer: return=minimal`, and the write answers `204 No
     * Content` — `200` carrying the ids only, for a batch. The row is the
     * default because it carries what the server decided: a serial id, an
     * `autoValue` timestamp, whatever `beforeSave` rewrote. A caller that
     * needs none of that is paying for a full row serialisation and, on
     * Postgres, a read-back per written row.
     *
     * Reach for it on imports and fire-and-forget writes. The method resolves
     * to `undefined` (or `[]`) when it is set, so a caller cannot accidentally
     * use a row the server never sent.
     */
    returning?: boolean;

    /**
     * The version of the row this write was made against, so it is refused if
     * the row has moved on.
     *
     * The `ETag` from the read that produced the row — `etagOf(row)` on a row
     * from `findById`, or the `ETag` response header. A mismatch answers `412`
     * rather than writing, which is the difference between "update the row I
     * read" and "overwrite whatever is there now". Without it a read, an edit
     * and a write is last-writer-wins over everything the write did not send,
     * and the loser is told nothing.
     *
     * `"*"` asserts only that the row exists.
     *
     * Honoured on `update` and `delete`.
     */
    ifMatch?: string;
}

export interface SDKCollectionClient<
    M extends Record<string, unknown> = Record<string, unknown>,
    I = Partial<M>,
    U = Partial<M>
> {
    /**
     * Find multiple records with optional filtering, pagination, and sorting.
     *
     * ## What a list method returns
     *
     * Two shapes, and one rule that tells them apart: **a window is wrapped, a
     * whole answer is not.**
     *
     * - `find()` and `listen()` return {@link FindResult} — `{ data, meta }` —
     *   because they hand back *one page*. `meta.total` and `meta.hasMore` are
     *   the caller's only way to know there is more, so a bare array would lose
     *   the answer to the question the call raises.
     * - `findAll()`, `createMany()` and `updateMany()` return a plain `M[]`,
     *   because there is nothing left over to report: the walk finished, or the
     *   batch is exactly the rows that were written. A `meta` there would be
     *   `{ total: rows.length, hasMore: false }`, which says nothing.
     * - `iterate()` yields rows one at a time and never materialises a list at
     *   all.
     *
     * So `data` is not a wrapper the SDK sometimes adds and sometimes forgets —
     * it is where the pagination metadata lives, and it is present exactly when
     * there is some.
     */
    find(params?: FindParams<M>): Promise<FindResult<M>>;

    /**
     * Walk every record matching a query, one row at a time, fetching pages as
     * the consumer consumes them.
     *
     * This is the pagination primitive: `find()` returns one window, `iterate()`
     * returns all of them without the caller hand-rolling the
     * `limit` / `offset += ` / "am I done yet" loop. Nothing is buffered — rows
     * are yielded as each page arrives, so a million-row walk costs one page of
     * memory. `break` stops the walk and no further requests are made.
     *
     * Termination is driven by the server's `meta.hasMore`, never by comparing
     * a page's length against the requested limit — a final page that happens
     * to be exactly full is indistinguishable that way, and a walk that stops
     * there drops rows. An empty page also ends the walk, and
     * {@link PageWalkOptions.maxPages} bounds a server that never stops saying
     * there is more.
     *
     * ## Consistency
     *
     * By default this pages by **offset**, which is only as stable as the table
     * is still: a row inserted or deleted ahead of the cursor between two
     * requests shifts every later window, so the walk can skip a row or hand
     * back the same one twice. That is inherent to offset paging, not a bug
     * here. On a collection with a unique sortable column, pass
     * {@link PageWalkOptions.cursor} to seek on it instead — the walk then
     * asks for rows strictly after the last one it saw, which concurrent writes
     * cannot perturb.
     *
     * @example
     * for await (const job of client.data.jobs.iterate({
     *     where: { status: ["==", "queued"] },
     *     cursor: "id",
     *     pageSize: 500
     * })) {
     *     await handle(job);
     * }
     */
    iterate(params?: IterateParams<M>): AsyncIterableIterator<M>;

    /**
     * {@link iterate}, collected into an array.
     *
     * Convenient when the result is known to be small and awkward to stream.
     * Because "known to be small" is an assumption and not a fact, the result is
     * capped — 10 000 rows by default — and going over the cap **throws**
     * rather than returning a short array that reads like a complete one. Raise
     * {@link FindAllParams.maxRows} when the data really is bigger, or switch to
     * `iterate()` and stream it.
     *
     * The offset-drift caveat on {@link iterate} applies here too.
     *
     * @throws When more rows match than `maxRows` allows.
     *
     * @example
     * const overdue = await client.data.invoices.findAll({
     *     where: { due_at: ["<", today] },
     *     cursor: "id"
     * });
     */
    findAll(params?: FindAllParams<M>): Promise<M[]>;

    /**
     * Find a single record by its ID.
     */
    findById(id: string | number): Promise<M | undefined>;

    /**
     * Read one record by its ID, or throw if it is not there.
     *
     * The counterpart to {@link findById}, and the one most reads want. A row
     * fetched by an id that came from a link, a route parameter or another row
     * is expected to exist; when it does not, that is the error case, not a
     * value to thread through the rest of the function.
     *
     * `findById` returns `M | undefined`, so every caller had to prove the row
     * existed before touching a field:
     *
     * ```ts
     * const post = await rebase.data.posts.findById(id);
     * post.title;                        // TS18048: 'post' is possibly 'undefined'
     * const ok = (await rebase.data.posts.findById(id))!.title;   // the `!` everyone reaches for
     * ```
     *
     * With `get`, the absent case is an exception with a code you can branch on,
     * and the happy path is typed as present:
     *
     * ```ts
     * const post = await rebase.data.posts.get(id);   // M, not M | undefined
     * ```
     *
     * Same split as Prisma's `findUnique` / `findUniqueOrThrow`: two contracts,
     * both wanted, named so the choice is visible at the call site.
     *
     * @throws {RebaseApiError} `NOT_FOUND` (status 404) when no such row exists,
     *   or is visible to the caller — row-level security makes a row the caller
     *   may not read indistinguishable from one that is not there, deliberately.
     */
    get(id: string | number): Promise<M>;

    /**
     * Create a new record.
     * @param data The record data to create (the collection's `Insert` shape).
     * @param id Optional specific id, sent as an `id` column. This is for tables
     *   whose key *is* `id`: the value goes in as that column. For a table keyed
     *   on anything else (a `sku`, a composite key), there is no `id` column to
     *   receive it — put the key in `data` instead, where it belongs among the
     *   columns.
     * @returns The created row
     */
    create(data: I, id?: string | number, options?: WriteOptions): Promise<M>;

    /**
     * Write many records in a single request and a single transaction.
     *
     * Built for imports and ETL, where one call per row means one HTTP round
     * trip and one transaction per row. Every record still runs the normal
     * pipeline — callbacks, relations, row-level security — and the batch is
     * all-or-nothing: if any record is rejected, none of them land and the
     * error names the offending index.
     *
     * A record carrying its primary key updates that row; one without inserts.
     * With `{ upsert: true }` each record is written as INSERT ... ON CONFLICT
     * DO UPDATE on the primary key instead, which is what makes a re-runnable
     * import idempotent.
     *
     * Batches are capped server-side (1000 rows by default) because one batch
     * holds its locks for the whole transaction — chunk larger jobs.
     *
     * Pass {@link WriteOptions.idempotencyKey} on anything that may be retried.
     * A client that never sees the response cannot know whether the batch
     * committed, and without a key the server cannot tell the retry from a
     * second genuine import — so it performs it again, duplicating every row in
     * the batch rather than just one.
     *
     * @returns The written rows, in the order given.
     *
     * @example
     * ```ts
     * for (const chunk of chunks(rows, 1000)) {
     *     await client.data.products.createMany(chunk, { upsert: true });
     * }
     * ```
     */
    createMany(data: I[], options?: { upsert?: boolean; onConflict?: readonly string[] } & WriteOptions): Promise<M[]>;

    /**
     * Update an existing record by ID.
     * @param data The fields to update (the collection's `Update` shape).
     * @param options Per-request write options — notably `idempotencyKey`.
     * @returns The updated row.
     * @throws {RebaseApiError} with status 404 when the record does not exist.
     *
     * `create`, `createMany`, `updateMany`, `delete` and `deleteMany` all took
     * {@link WriteOptions}; this one did not, so the single-row update was the
     * one write on the surface that could not be made idempotent. A client that
     * never sees the response retries, and without a key the server cannot tell
     * that retry from a second deliberate edit — which on a `PATCH` that
     * increments or appends is a second edit applied.
     */
    update(id: string | number, data: U | UpdateValues<U>, options?: WriteOptions): Promise<M>;

    /**
     * Insert the row, or replace the one already occupying its key.
     *
     * `INSERT ... ON CONFLICT DO UPDATE`, in one statement — so unlike a
     * `findById` followed by `create`-or-`update` it cannot lose the race
     * between the two, and unlike `create` it does not fail when the row is
     * already there. That is what makes a re-runnable import idempotent
     * without a key.
     *
     * The conflict target defaults to the primary key. Pass `onConflict` to
     * upsert on a natural key instead — `["email"]`, `["tenant_id", "slug"]` —
     * and the columns must carry a uniqueness guarantee the database can use:
     * a property with `validation.unique`, or the columns of a declared
     * `unique` index. Anything else is a 400 rather than a Postgres error
     * raised half-way through a transaction.
     *
     * The `on_create` timestamp of a row that already existed is left alone: a
     * conflict means the row's creation is a fact about the past, and a nightly
     * re-import that reset `createdAt` on everything it touched would take
     * every "new this week" query with it.
     *
     * @example
     * ```ts
     * await client.data.users.upsert(
     *     { email: "a@b.c", name: "Ada" },
     *     { onConflict: ["email"] }
     * );
     * ```
     */
    upsert(data: I, options?: UpsertOptions): Promise<M>;

    /**
     * Update many records in a single request and a single transaction.
     *
     * The counterpart to {@link createMany}, and the reason it exists is the
     * same: one call per row means one HTTP round trip and one transaction per
     * row. Every record still runs the normal pipeline — callbacks, relations,
     * row-level security — and the batch is all-or-nothing, so a rejected
     * record leaves none of them written and the error names the offending
     * index.
     *
     * Each entry is `{ id, data }` rather than a flat row carrying its own key.
     * That is deliberate: on a table keyed on something other than `id` — a
     * `sku`, a composite key — a flat row cannot say whether a column is the
     * address or a value to write. Naming the address separately mirrors
     * single-row `update(id, data)` exactly and leaves nothing to infer.
     *
     * An id that matches no row fails the batch with a 404 rather than being
     * skipped, for the same reason `update()` does: silently updating four of
     * five rows is worse than updating none.
     *
     * Batches share `createMany`'s server-side cap (1000 rows by default),
     * because one batch holds its locks for the whole transaction.
     *
     * Pass {@link WriteOptions.idempotencyKey} on anything that may be retried.
     * An update replayed in full is naturally idempotent, but one interleaved
     * with another writer's is not — the key is what stops a lost ACK from
     * re-applying a stale batch over newer data.
     *
     * @returns The updated rows, in the order given.
     *
     * @example
     * ```ts
     * await client.data.orders.updateMany([
     *     { id: "o-1", data: { status: "shipped" } },
     *     { id: "o-2", data: { status: "shipped" } }
     * ]);
     * ```
     */
    updateMany(updates: { id: string | number; data: U | UpdateValues<U> }[], options?: WriteOptions): Promise<M[]>;

    /**
     * Delete a record by ID.
     * @throws {RebaseApiError} with status 404 when the record does not exist.
     *
     * Takes {@link WriteOptions} like every other write. It did not, so the one
     * mutation that cannot be made safe by repeating it — a delete replayed
     * after the row is gone answers 404, which an offline queue reads as a
     * permanent failure — was also the one that could not carry an
     * `idempotencyKey`.
     */
    delete(id: string | number, options?: WriteOptions): Promise<void>;

    /**
     * Delete many records in a single request and a single transaction.
     *
     * Takes ids, not a filter. A filter-shaped bulk delete is a different and
     * far more dangerous operation — the failure mode is an omitted or
     * mistyped condition emptying a table, and it cannot be reviewed at the
     * call site the way an explicit list can. Read first, then pass the ids you
     * meant.
     *
     * `beforeDelete` and `afterDelete` fire per row, exactly as they do for
     * single deletes, and returning `false` from `beforeDelete` fails the batch
     * rather than quietly dropping one row from it. All-or-nothing, so an id
     * that matches no row 404s the whole call.
     *
     * Shares `createMany`'s row cap.
     *
     * @example
     * ```ts
     * const stale = await client.data.sessions.findAll({
     *     where: { expires_at: ["<", cutoff] }
     * });
     * await client.data.sessions.deleteMany(stale.map(s => s.id as string));
     * ```
     */
    deleteMany(ids: (string | number)[], options?: WriteOptions): Promise<void>;

    /**
     * The low-level realtime subscription: raw server pushes, nothing else.
     *
     * **Prefer `observe()`** on a client from `@rebasepro/client`, which wraps
     * this one and is what a UI actually wants — it emits from the local
     * database first when offline is enabled, re-emits on local writes and
     * rollbacks, and de-duplicates emissions so a refresh that changes nothing
     * does not call back. `listen` does none of that; it forwards what the
     * socket sends.
     *
     * Always present. A client that cannot subscribe — one built with
     * `realtime: false`, or on a driver with no `listenCollection` — installs a
     * stub that throws a `RebaseClientError` naming the configuration that
     * would make it work. It used to be optional, which made every call site
     * either write `listen!(…)` or a null check the type system could not tell
     * apart from a real capability question; the answer to *that* question is
     * {@link isUnsupported}, and the answer for ordinary code is to just call
     * it.
     *
     * `observe()` degrades to a single fetch instead of throwing, which is the
     * other reason to reach for it instead.
     */
    listen(params: FindParams<M> | undefined, onUpdate: (response: FindResult<M>) => void, onError?: (error: Error) => void): () => void;

    /** {@link listen} for a single row. Prefer `observeById()`. */
    listenById(id: string | number, onUpdate: (row: M | undefined) => void, onError?: (error: Error) => void): () => void;

    /**
     * Count the number of records matching the given filter.
     *
     * Always present; see {@link listen} for what a transport that cannot serve
     * it does instead.
     */
    count(params?: FindParams<M>): Promise<number>;

    /**
     * `count`/`sum`/`avg`/`min`/`max` over the matching rows, optionally
     * grouped — the SDK half of `GET /<collection>/aggregate`.
     *
     * The whole point is not to fetch rows in order to reduce them: "revenue by
     * status" over a million orders is one query and one row per status here,
     * and a `findAll()` plus a loop everywhere else — which is wrong under a
     * `limit` and unaffordable without one. It runs through the same
     * request-scoped handle as every other read, so RLS applies to the rows
     * being aggregated.
     *
     * ```ts
     * const rows = await rebase.data.orders.aggregate({
     *     select: [{ fn: "sum", field: "total" }, { fn: "count" }],
     *     groupBy: ["status"],
     *     where: { created_at: [">=", startOfMonth] }
     * });
     * // [{ status: "paid", sum_total: 41822.5, count: 317 }, …]
     * ```
     *
     * Always present; a backend whose driver cannot aggregate answers 501
     * naming the capability rather than an empty result set, which would read
     * as "nothing matched".
     */
    aggregate(params: AggregateParams<M>): Promise<AggregateRow[]>;

    // Fluent Query Builder
    where<K extends keyof M & string, Op extends WhereFilterOp>(column: K, operator: Op, value: WhereValueFor<Op, M[K]>): SDKQueryBuilderInterface<M>;
    /** A relation path (`author.name`) or a JSON path (`metadata->>tier`). */
    where(column: NonColumnFieldPath, operator: WhereFilterOp, value: unknown): SDKQueryBuilderInterface<M>;
    where(logicalCondition: LogicalCondition): SDKQueryBuilderInterface<M>;
    orderBy(
        column: FieldPath<M> | ComputedSortField | RelationAggregateSort,
        direction?: "asc" | "desc",
        nulls?: NullsPlacement
    ): SDKQueryBuilderInterface<M>;
    limit(count: number): SDKQueryBuilderInterface<M>;
    offset(count: number): SDKQueryBuilderInterface<M>;
    search(searchString: string, options?: { explain?: boolean }): SDKQueryBuilderInterface<M>;
    /**
     * Order rows by nearest-neighbour distance to `vector`, closest first.
     * Postgres only, over a `type: "vector"` property. See
     * {@link SDKQueryBuilderInterface.vectorSearch}.
     */
    vectorSearch(
        property: string,
        vector: number[],
        options?: { distance?: "cosine" | "l2" | "inner_product"; threshold?: number }
    ): SDKQueryBuilderInterface<M>;
    include(...relations: (string | IncludeSpec)[]): SDKQueryBuilderInterface<M>;
    /** {@link SDKQueryBuilderInterface.fields} */
    fields(...columns: (FieldPath<M> | string)[]): SDKQueryBuilderInterface<M>;
    /** {@link SDKQueryBuilderInterface.distinct} */
    distinct(enabled?: boolean): SDKQueryBuilderInterface<M>;
    /** {@link SDKQueryBuilderInterface.after} */
    after(cursor: string): SDKQueryBuilderInterface<M>;
}

/**
 * The unified data access object for the **admin panel** (Entity-shaped).
 *
 * Access collections as dynamic properties: `data.products.find(...)`. Each
 * accessor returns `Entity`-wrapped records (`{ id, path, values }`) — the
 * view-model the admin renders. This is what `useData()` / the admin
 * `RebaseContext.data` are backed by.
 *
 * @internal App developers do **not** use this — they use
 * {@link RebaseSdkData} (flat rows), which is what the SDK client and backend
 * `context.data` expose. This Entity-shaped map backs the admin panel only.
 *
 * @group Data
 */
export type RebaseData<DB = unknown> = {
    /**
     * Get a collection accessor by slug.
     * Alternative to dynamic property access for cases where
     * the collection name is a variable.
     *
     * @example
     * const accessor = data.collection("products");
     * await accessor.find({ limit: 10 });
     */
    collection<M extends Record<string, unknown> = Record<string, unknown>>(slug: string): CollectionAccessor<M>;
} & (
    DB extends Record<string, unknown>
        ? { [K in keyof DB]: CollectionAccessor<DB[K] extends { Row: infer R extends Record<string, unknown> } ? R : Record<string, unknown>> }
        : {
            /**
             * Dynamic collection accessor.
             * Access any collection by its slug as a property.
             *
             * The index signature is `CollectionAccessor` alone, for the reason
             * spelled out on {@link RebaseSdkData}: unioning in the `collection`
             * method's own signature is unnecessary across an intersection, and it
             * costs `data.products.find()` — the access this `@example` documents.
             *
             * @example
             * data.products.find({ where: { status: ["==", "published"] } })
             */
            [collectionSlug: string]: CollectionAccessor;
        }
);

/**
 * The unified data access object for the **SDK** — flat rows, no Entity wrapper.
 *
 * This is the symmetric developer-facing data API, identical in shape on both
 * sides of the stack:
 * - The frontend SDK client (`client.data.products.find()`)
 * - Backend framework callbacks & scripts (`context.data.products.find()`)
 *
 * Every accessor returns flat rows (the table's columns) via
 * {@link SDKCollectionClient} — access fields directly (`row.title`), never
 * `row.values.title`. The admin uses {@link RebaseData} (Entity) instead.
 *
 * @example
 * // Frontend SDK
 * const { data: posts } = await client.data.posts.find();
 * console.log(posts[0].title);        // flat — no .values
 *
 * // Backend callback — identical shape
 * callbacks: {
 *   beforeSave: async ({ context }) => {
 *     const product = await context.data.products.findById(id);
 *     console.log(product?.price);     // flat — no .values
 *   }
 * }
 *
 * @group Data
 */
/**
 * Extract the `Row` shape from a generated `Database[slug]` entry, falling
 * back to an open record when the entry is untyped.
 * @group Data
 */
export type RowOf<T> = T extends { Row: infer R extends Record<string, unknown> } ? R : Record<string, unknown>;

/**
 * Extract the `Insert` shape from a generated `Database[slug]` entry (the
 * input accepted by `create`), falling back to `Partial<Row>`.
 * @group Data
 */
export type InsertOf<T> = T extends { Insert: infer I extends Record<string, unknown> } ? I : Partial<RowOf<T>>;

/**
 * Extract the `Update` shape from a generated `Database[slug]` entry (the
 * input accepted by `update`), falling back to `Partial<Row>`.
 * @group Data
 */
export type UpdateOf<T> = T extends { Update: infer U extends Record<string, unknown> } ? U : Partial<RowOf<T>>;

/**
 * Note on the untyped branch below: its index signature is
 * `SDKCollectionClient`, NOT `SDKCollectionClient | ((slug: string) => …)`.
 *
 * The union looks like it is needed so `collection` — a method on this same
 * object — satisfies the index signature. It is not, because `collection` is
 * declared in a *separate* member of the intersection, and TypeScript only
 * requires named properties to be assignable to an index signature declared
 * alongside them. Including the function arm cost the documented accessor:
 *
 *     rebase.dataAsAdmin.projects.find()
 *     //                          ^ Property 'find' does not exist on type
 *     //                            'SDKCollectionClient | ((slug: string) => …)'
 *
 * Every project without a generated `Database` type lands on this branch, so
 * property-style access — the form used by the `@example` below, by the
 * scaffolded function template, and by the 0.13 migration note — did not
 * compile for any of them. Do not restore the arm; use `collection(slug)` if a
 * caller genuinely needs the by-slug function.
 */
export type RebaseSdkData<DB = unknown> = {
    /**
     * Get a flat collection accessor by slug.
     *
     * @example
     * const accessor = data.collection("products");
     * await accessor.find({ limit: 10 });
     */
    collection<M extends Record<string, unknown> = Record<string, unknown>>(slug: string): SDKCollectionClient<M>;
} & (
    DB extends Record<string, unknown>
        ? { [K in keyof DB]: SDKCollectionClient<RowOf<DB[K]>, InsertOf<DB[K]>, UpdateOf<DB[K]>> }
        : {
            /**
             * Dynamic flat collection accessor.
             * Access any collection by its slug as a property.
             *
             * @example
             * data.products.find({ where: { status: ["==", "published"] } })
             */
            [collectionSlug: string]: SDKCollectionClient;
        }
);
